import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  Pagination,
  InputAdornment,
  Autocomplete,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  ListSubheader,
  Snackbar,
  alpha,
  LinearProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Info as InfoIcon,
  ShoppingCart as ShoppingCartIcon,
  Mail as MailIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  FileDownload as DownloadIcon,
  Visibility as VisibilityIcon,
  ArrowDropDown as ArrowDropDownIcon,
  MedicalServices as MedicalServicesIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  RestartAlt as ResetIcon,
  LocalShipping as LocalShippingIcon,
} from "@mui/icons-material";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { formatToUIDate, formatToUIDateTime, formatToUIDateTimeLong } from "../utils/dateUtils";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import OrderMedicationService from "../services/OrderMedicationService";
import { useResidents } from "../hooks/useResidents";

import { useAuth } from "../contexts/AuthContext";
import { useTenant } from "../contexts/TenantContext";
import {
  MedicationOrderDTO,
  MedicationRoute,
  MedicationOrderStatus,
} from "../types/medication.types";
import residentService from "../services/residentService";
import userService from "../services/userService";
import { ResidentDTO } from "../types/resident.types";
import SignatureInput from "../components/SignatureInput";
import apiClient from "../services/apiClient";
import masterDataService from "../services/masterDataService";
import {
  calculateMedicationQuantity,
  getNextQuantityOnFieldChange,
} from "../utils/medicationQuantityUtils";

// Valid medication route values
const VALID_ROUTES = [
  "PO", "SL", "BUCCAL", "NG_GT", "JT", "PR",
  "IV", "IM", "SC", "ID", "IO", "IT", "IA", "IP",
  "TOP", "TD", "INTRADERM", "WOUND", "OTIC", "OPHTH",
  "INH", "NEB", "NASAL",
  "VAG", "URETHRAL", "RECTAL",
  "EPIDURAL", "INTRACARDIAC", "INTRAOCULAR", "INTRAPLEURAL", "INTRAVESICAL",
];

const ROUTE_MAPPING: Record<string, string> = {
  ORAL: "PO",
  "ORAL (PO)": "PO",
  PO: "PO",
  SL: "SL",
  SUBLINGUAL: "SL",
  BUCCAL: "BUCCAL",
  IV: "IV",
  INTRAVENOUS: "IV",
  IM: "IM",
  INTRAMUSCULAR: "IM",
  SC: "SC",
  SQ: "SC",
  SUBCUTANEOUS: "SC",
  TOP: "TOP",
  TOPICAL: "TOP",
  TD: "TD",
  TRANSDERMAL: "TD",
  NASAL: "NASAL",
  OTIC: "OTIC",
  OPHTH: "OPHTH",
  OPHTHALMIC: "OPHTH",
  RECTAL: "PR",
  PR: "PR",
  INH: "INH",
  INHALATION: "INH",
  NEB: "NEB",
  NEBULIZER: "NEB",
};

const normalizeMedicationRoute = (routeStr?: string): string => {
  if (!routeStr) return "";
  const normalized = routeStr.trim().toUpperCase();
  if (!normalized) return "";

  // Prefer route code shown inside parentheses, e.g. "Oral (PO)".
  const parenMatch = normalized.match(/\(([^)]+)\)/);
  if (parenMatch?.[1]) {
    const parenCode = parenMatch[1].trim().toUpperCase();
    if (VALID_ROUTES.includes(parenCode)) return parenCode;
    if (ROUTE_MAPPING[parenCode]) return ROUTE_MAPPING[parenCode];
  }

  if (VALID_ROUTES.includes(normalized)) return normalized;
  if (ROUTE_MAPPING[normalized]) return ROUTE_MAPPING[normalized];

  // Fallback: split labels like "SUBCUTANEOUS / SC" and try each token.
  const tokens = normalized.split(/[^A-Z0-9_]+/).filter(Boolean);
  for (const token of tokens) {
    if (VALID_ROUTES.includes(token)) return token;
    if (ROUTE_MAPPING[token]) return ROUTE_MAPPING[token];
  }

  return "";
};



const medicationItemSchema = yup.object({
  medicationName: yup.string().required("Medication name is required"),
  dosage: yup.string().required("Dosage is required"),
  route: yup
    .string()
    .transform((value) => normalizeMedicationRoute(value))
    .required("Route is required")
    .oneOf(VALID_ROUTES, "Invalid route selected"),
  frequency: yup.string().required("Frequency is required"),
  startDate: yup
    .date()
    .required("Medication start date is required")
    .nullable(),
  endDate: yup.date().nullable(),
  medicationExpirationDate: yup.date().nullable(),
  note: yup.string().nullable(),
  quantity: yup.string().nullable(),
  refillsAuthorized: yup.number().nullable(),
});

const orderSchema = yup.object({
  residentId: yup.string().required("Resident is required"),
  prescribingPhysician: yup
    .string()
    .required("Prescribing physician is required"),
  pharmacyName: yup
    .string()
    .trim()
    .required("Pharmacy name is required"),
  pharmacyEmail: yup
    .string()
    .email("Invalid email")
    .required("Pharmacy email is required"),
  pharmacyContactNumber: yup
    .string()
    .required("Pharmacy contact number is required")
    .length(10, "Phone number must be exactly 10 digits")
    .matches(/^[0-9]+$/, "Phone number must contain only digits"),
  prescriptionNumber: yup
    .string()
    .test('is-number', 'Only numbers are allowed', value => !value || /^[0-9]*$/.test(value))
    .max(10, "Prescription number cannot exceed 10 characters")
    .nullable(),
  medicationItems: yup
    .array()
    .of(medicationItemSchema)
    .min(1, "At least one medication is required"),
  prescriptionFile: yup
    .mixed<File>()
    .nullable()
    .test(
      "fileSize",
      "Prescription file size cannot exceed 5MB.",
      (value: any) => {
        if (!value) return true;
        return value.size <= 5 * 1024 * 1024;
      }
    ),
  nextRefillDate: yup.date().nullable(),
});

type OrderFormData = yup.InferType<typeof orderSchema>;

interface MedicationEntry {
  medicationName: string;
  dosage: string;
  route: MedicationRoute | "";
  frequency: string;
  startDate: Date | null;
  endDate: Date | null;
  medicationExpirationDate: Date | null;
  note: string;
  quantity?: string;
  refillsAuthorized?: number;
}

const standardFrequencies = [
  "Once daily",
  "Twice daily",
  "Three times daily",
  "Four times daily",
  "Every 4 hours",
  "Every 6 hours",
  "Every 8 hours",
  "Weekly",
  "Every 2 weeks",
  "Every 3 weeks",
  "AC breakfast",
  "PC breakfast",
  "AC dinner",
  "PC dinner",
  "AC lunch",
  "PC lunch",
  "As needed PRN",
  "At bedtime",
  "Evening time",
  "Other",
];

const formatStatus = (status: string) => {
  if (!status) return "";
  const s = status.toUpperCase();
  if (s === "PARTIALLY_RECEIVED") return "Partially Received";
  if (s === "RECEIVED") return "Received";
  if (s === "ACTIVE") return "Active";
  if (s === "CANCELLED") return "Cancelled";
  if (s === "ORDER_MAIL_SENT") return "Order Mail Sent";
  if (s === "PENDING") return "Pending Delivery";
  return status
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

const renderDeliveryProgress = (med: MedicationOrderDTO) => {
  const items = med.medicationItems || [];
  if (!items.length) {
    return <Typography variant="caption" sx={{ color: "text.secondary" }}>No items</Typography>;
  }

  const isFullReceivedStatus = med.status === "RECEIVED";
  const totalOrdered = items.reduce((sum, it) => sum + (parseInt(it.quantity || "0", 10) || 0), 0);
  const totalReceived = items.reduce((sum, it) => {
    if (typeof it.receivedQuantity === "number") return sum + it.receivedQuantity;
    if (isFullReceivedStatus) return sum + (parseInt(it.quantity || "0", 10) || 0);
    return sum;
  }, 0);

  const percentage = totalOrdered > 0 ? Math.min(100, Math.round((totalReceived / totalOrdered) * 100)) : (isFullReceivedStatus ? 100 : 0);
  const isComplete = (totalReceived >= totalOrdered && totalOrdered > 0) || isFullReceivedStatus;

  return (
    <Box sx={{ minWidth: 140 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            fontSize: "0.74rem",
            color: isComplete ? "#15803D" : totalReceived > 0 ? "#B45309" : "#64748B",
          }}
        >
          {totalReceived} / {totalOrdered} Received
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            fontSize: "0.74rem",
            color: isComplete ? "#15803D" : totalReceived > 0 ? "#B45309" : "#64748B",
          }}
        >
          {percentage}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 6,
          borderRadius: 3,
          bgcolor: "#E2E8F0",
          "& .MuiLinearProgress-bar": {
            bgcolor: isComplete ? "#16A34A" : totalReceived > 0 ? "#F59E0B" : "#94A3B8",
            borderRadius: 3,
          },
        }}
      />
    </Box>
  );
};

const OrderMedicationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lastAutoFilledQtyRef = useRef<string>("");
  const [openDialog, setOpenDialog] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [imagePreviewSrc, setImagePreviewSrc] = useState<string | null>(null);
  const [medications, setMedications] = useState<MedicationOrderDTO[]>([]);
  const [frequencyOptions, setFrequencyOptions] = useState<{ id: string; valueName: string; orderNumber: number }[]>([]);
  const [routeOptions, setRouteOptions] = useState<{ id: string; valueName: string; orderNumber: number }[]>([]);
  const [residentMasterMedications, setResidentMasterMedications] = useState<
    any[]
  >([]);
  const [fullMedicationsCache, setFullMedicationsCache] = useState<
    MedicationOrderDTO[]
  >([]); // Cache for the full facility list
  const [loading, setLoading] = useState(false);

  // Helper to normalize route string to MedicationRoute enum
  const normalizeRoute = (routeStr?: string): MedicationRoute | "" => {
    const normalized = normalizeMedicationRoute(routeStr);
    return (normalized as MedicationRoute) || "";
  };
  const [loadingAction, setLoadingAction] = useState<{
    id: string | null;
    type: "SEND" | "DELETE" | "CREATE";
  } | null>(null);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<string | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [fetchedOrderDetails, setFetchedOrderDetails] =
    useState<MedicationOrderDTO | null>(null);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Receive Stock Modal State
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [receivingOrder, setReceivingOrder] = useState<MedicationOrderDTO | null>(null);
  const [receiveQuantities, setReceiveQuantities] = useState<Record<string, number | "">>({});
  const [receiveNotes, setReceiveNotes] = useState<string>("");
  const [receiveSubmitting, setReceiveSubmitting] = useState(false);
  const [receiveErrors, setReceiveErrors] = useState<Record<string, string>>({});
  const [receiveModalError, setReceiveModalError] = useState<string | null>(null);

  const handleOpenReceiveModal = (order: MedicationOrderDTO) => {
    setReceivingOrder(order);
    const initialQuantities: Record<string, number | ""> = {};
    (order.medicationItems || []).forEach((item) => {
      const ordered = parseInt(item.quantity || "0", 10) || 0;
      const receivedSoFar = typeof item.receivedQuantity === "number" ? item.receivedQuantity : 0;
      const remaining = typeof item.remainingQuantity === "number"
        ? item.remainingQuantity
        : Math.max(0, ordered - receivedSoFar);

      const idKey = item.id || item.medicationName;
      initialQuantities[idKey] = remaining;
    });

    setReceiveQuantities(initialQuantities);
    setReceiveNotes("");
    setReceiveErrors({});
    setReceiveModalError(null);
    setReceiveModalOpen(true);
  };

  const handleReceiveAll = () => {
    if (!receivingOrder) return;
    const allRemaining: Record<string, number | ""> = {};
    (receivingOrder.medicationItems || []).forEach((item) => {
      const ordered = parseInt(item.quantity || "0", 10) || 0;
      const receivedSoFar = typeof item.receivedQuantity === "number" ? item.receivedQuantity : 0;
      const remaining = typeof item.remainingQuantity === "number"
        ? item.remainingQuantity
        : Math.max(0, ordered - receivedSoFar);
      const idKey = item.id || item.medicationName;
      allRemaining[idKey] = remaining;
    });
    setReceiveQuantities(allRemaining);
    setReceiveErrors({});
  };

  const handleClearQuantities = () => {
    if (!receivingOrder) return;
    const cleared: Record<string, number | ""> = {};
    (receivingOrder.medicationItems || []).forEach((item) => {
      const idKey = item.id || item.medicationName;
      cleared[idKey] = 0;
    });
    setReceiveQuantities(cleared);
    setReceiveErrors({});
  };

  const handleConfirmReceive = async () => {
    if (!receivingOrder || !receivingOrder.id) return;

    const items = receivingOrder.medicationItems || [];
    const errors: Record<string, string> = {};
    let totalReceiving = 0;

    const requestItems: { itemId: string; receivedQuantity: number }[] = [];

    items.forEach((item) => {
      const idKey = item.id || item.medicationName;
      const rawVal = receiveQuantities[idKey];
      const val = typeof rawVal === "number" ? rawVal : 0;
      const ordered = parseInt(item.quantity || "0", 10) || 0;
      const receivedSoFar = typeof item.receivedQuantity === "number" ? item.receivedQuantity : 0;
      const remaining = typeof item.remainingQuantity === "number"
        ? item.remainingQuantity
        : Math.max(0, ordered - receivedSoFar);

      if (val < 0) {
        errors[idKey] = "Cannot be negative";
      } else if (val > remaining) {
        errors[idKey] = `Max is ${remaining}`;
      } else {
        totalReceiving += val;
        if (val > 0 && item.id) {
          requestItems.push({
            itemId: item.id,
            receivedQuantity: val,
          });
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setReceiveErrors(errors);
      return;
    }

    if (totalReceiving <= 0) {
      setReceiveModalError("Please enter a quantity greater than 0 for at least one medication.");
      return;
    }

    try {
      setReceiveSubmitting(true);
      setReceiveModalError(null);

      const payload = {
        items: requestItems,
        notes: receiveNotes.trim() || undefined,
      };

      await OrderMedicationService.receiveOrder(receivingOrder.id, payload);

      setSuccessMessage("Medication order stock received successfully!");
      setReceiveModalOpen(false);
      setReceivingOrder(null);
      setReceiveModalError(null);

      // Refresh list
      await fetchMedications(true);

      // If viewing modal is currently viewing this order, update it too
      if (viewingId === receivingOrder.id) {
        const refreshed = await OrderMedicationService.getOrderById(receivingOrder.id);
        setFetchedOrderDetails(refreshed);
      }
    } catch (err: any) {
      console.error("Failed to receive medication stock:", err);
      setReceiveModalError(err.message || "Failed to receive medication order stock");
    } finally {
      setReceiveSubmitting(false);
    }
  };

  // Auto-clear success messages after a short delay so they don't linger
  useEffect(() => {
    if (!successMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage(null);
    }, 5000); // 5 seconds

    return () => clearTimeout(timer);
  }, [successMessage]);

  // Auto-clear error messages after a short delay so they don't linger
  useEffect(() => {
    if (!error) return;

    const timer = setTimeout(() => {
      setError(null);
    }, 5000); // 5 seconds

    return () => clearTimeout(timer);
  }, [error]);

  // State for adding a single medication item row
  const [newMedItem, setNewMedItem] = useState<MedicationEntry>({
    medicationName: "",
    dosage: "",
    route: "",
    frequency: "",
    startDate: new Date(),
    endDate: null,
    medicationExpirationDate: null,
    note: "",
    quantity: "",
    refillsAuthorized: 0,
  });
  const [newMedItemErrors, setNewMedItemErrors] = useState<{
    medicationName?: string;
    dosage?: string;
    frequency?: string;
    route?: string;
  }>({});
  const [printingOrderId, setPrintingOrderId] = useState<string | null>(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureOrderId, setSignatureOrderId] = useState<string | null>(null);
  const [signingLoading, setSigningLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const localFileUrlsRef = useRef<
    Record<string, { url: string; type?: string }>
  >({});

  // Search term for API search
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Debounce timer ref
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(15);

  // Status Filter - Initialize from state if navigated from Dashboard
  const [statusFilter, setStatusFilter] = useState<string>(location.state?.statusFilter || "ALL");

  const { currentTenantId } = useTenant();

  const [residents, setResidents] = useState<ResidentDTO[]>([]);
  const [residentsLoading, setResidentsLoading] = useState(false);


  const { user: currentUser } = useAuth();

  // Sort residents alphabetically by name
  const sortedResidents = useMemo(() => {
    return [...residents].sort((a: any, b: any) => {
      const nameA = (a.residentName || `${a.firstName || ""} ${a.lastName || ""}`).toLowerCase().trim();
      const nameB = (b.residentName || `${b.firstName || ""} ${b.lastName || ""}`).toLowerCase().trim();
      return nameA.localeCompare(nameB);
    });
  }, [residents]);

  const handleCancelPrintOrder = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setPrintingOrderId(null);
  };

  const handlePrintOrder = async (id: string) => {
    // Global cancellation: if anything is printing, cancel it first.
    if (printingOrderId) {
      handleCancelPrintOrder();
      // If it was the SAME order, we just stop.
      if (printingOrderId === id) return;
    }

    setPrintingOrderId(id);
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const blob = await OrderMedicationService.getOrderPdf(
        id,
        controller.signal,
      );
      if (controller.signal.aborted) return;

      const url = window.URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      iframe.style.visibility = "hidden";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "1px";
      iframe.style.height = "1px";
      iframe.style.border = "0";
      iframe.src = url;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        if (controller.signal.aborted) {
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
          window.URL.revokeObjectURL(url);
          return;
        }

        setPrintingOrderId(null);
        abortControllerRef.current = null;

        try {
          if (iframe.contentWindow) {
            iframe.contentWindow.onafterprint = () => {
              if (document.body.contains(iframe)) document.body.removeChild(iframe);
              try { window.URL.revokeObjectURL(url); } catch(e) {}
            };
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          }
        } catch (printErr) {
          console.error("Direct print failed:", printErr);
          // Removed fallback to ensure cancel stays cancel
        }
      };

      setTimeout(() => {
        if (printingOrderId === id) {
          setPrintingOrderId(null);
          abortControllerRef.current = null;
        }
      }, 10000);

      // Fallback cleanup in case onafterprint doesn't fire, set to 5 minutes
      setTimeout(() => {
        if (document.body.contains(iframe)) document.body.removeChild(iframe);
        try { window.URL.revokeObjectURL(url); } catch(e) {}
      }, 300000);
    } catch (err: any) {
      if (err.name === "AbortError") {
        console.log("Print request cancelled");
      } else {
        console.error("Failed to print medication order PDF:", err);
      }
      setPrintingOrderId(null);
      abortControllerRef.current = null;
    }
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<OrderFormData>({
    resolver: yupResolver(orderSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      residentId: "",
      prescribingPhysician: "",
      pharmacyName: "",
      pharmacyEmail: "",
      pharmacyContactNumber: "",
      prescriptionNumber: "",
      medicationItems: [],
      prescriptionFile: null,
    },
  });

  // Clear global error when any field in the new medication row changes
  useEffect(() => {
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newMedItem]);

  // Clear global error when any form value changes
  useEffect(() => {
    if (!openDialog) return;
    const subscription = watch(() => {
      setError(null);
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, openDialog]);

  // Synchronize field-level errors with row values to ensure they clear automatically
  useEffect(() => {
    setNewMedItemErrors((prev) => {
      let hasChange = false;
      const next = { ...prev };
      if (newMedItem.medicationName && next.medicationName) {
        delete next.medicationName;
        hasChange = true;
      }
      if (newMedItem.dosage && next.dosage) {
        delete next.dosage;
        hasChange = true;
      }
      if (newMedItem.frequency && next.frequency) {
        delete next.frequency;
        hasChange = true;
      }
      if (newMedItem.route && next.route) {
        delete next.route;
        hasChange = true;
      }
      return hasChange ? next : prev;
    });
  }, [newMedItem]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "medicationItems",
  });
  const residentId = watch("residentId");
  const medicationItemsWatch = watch("medicationItems");

  const availableMedicationOptions = React.useMemo(() => {
    const allNames = Array.from(
      new Set(residentMasterMedications.map((m) => m.medicationName)),
    );
    const addedNames = (medicationItemsWatch || [])
      .map((it: any) => it?.medicationName)
      .filter(Boolean);
    return allNames.filter((n) => !addedNames.includes(n));
  }, [residentMasterMedications, medicationItemsWatch]);

  // Fetch medications from service
  const fetchMedications = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        setError(null);
        let meds: MedicationOrderDTO[] = [];
        if (currentTenantId) {
          const result =
            await OrderMedicationService.getOrdersByFacility(currentTenantId);

          if (Array.isArray(result)) {
            meds = result;
          } else if (
            (result as any).content &&
            Array.isArray((result as any).content)
          ) {
            meds = (result as any).content;
          } else {
            meds = [];
          }
        } else {
          const response = await OrderMedicationService.getAllOrders(0, 100);
          meds = response.content || [];
        }

        const sortedMedications = [...meds].sort((a, b) => {
          const getSortTime = (item: MedicationOrderDTO) => {
            // If createdAt is present, use it
            if (item.createdAt) return new Date(item.createdAt).getTime();
            // Fallback to other dates
            if (item.orderedAt) return new Date(item.orderedAt).getTime();
            if (item.updatedAt) return new Date(item.updatedAt).getTime();

            // If no date is available, assume it's a brand new optimistic item
            // and prioritize it at the top
            return Number.MAX_SAFE_INTEGER;
          };

          const timeA = getSortTime(a);
          const timeB = getSortTime(b);

          return timeB - timeA;
        });
        setMedications(sortedMedications);
        setFullMedicationsCache(sortedMedications);
      } catch (err: any) {
        setError(err.message || "Failed to fetch medication orders");
        console.error("Error fetching medications:", err);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [currentTenantId],
  );

  const filteredMedications = React.useMemo(() => {
    let items = [...medications];

    // Filter by Status first if not "ALL"
    if (statusFilter !== "ALL") {
      items = items.filter((med) => med.status === statusFilter);
    }

    return items;
  }, [medications, statusFilter]);

  const searchMedications = useCallback(async () => {
    if (!currentTenantId) return;

    const term = searchTerm.trim();
    if (!term) {
      setIsSearchActive(false);
      return;
    }

    try {
      setSearchLoading(true);
      setIsSearchActive(true);

      const results = await OrderMedicationService.searchOrders({
        facilityId: currentTenantId,
        searchTerm: term,
      });

      let meds: MedicationOrderDTO[] = [];
      if (Array.isArray(results)) {
        meds = results;
      } else if (
        (results as any)?.content &&
        Array.isArray((results as any).content)
      ) {
        meds = (results as any).content;
      }

      // Only update if search results are found, otherwise stay with cache+local filter
      if (meds.length > 0) {
        const sortedSearchMeds = [...meds].sort((a: any, b: any) => {
          const timeA = new Date(a.createdAt || a.orderedAt || 0).getTime();
          const timeB = new Date(b.createdAt || b.orderedAt || 0).getTime();
          return timeB - timeA;
        });
        setMedications(sortedSearchMeds);
      }
    } catch (err) {
      console.error("Background search refresh failed:", err);
    } finally {
      setSearchLoading(false);
    }
  }, [currentTenantId, searchTerm]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setIsSearchActive(false);
      setMedications(fullMedicationsCache);
      return;
    }

    // Set search to inactive while typing to trigger local filtering
    // until the new API search results come in
    setIsSearchActive(false);

    const timer = setTimeout(() => {
      searchMedications();
    }, 600);

    return () => clearTimeout(timer);
  }, [searchTerm, searchMedications, fullMedicationsCache]);

  // Restore local file URLs from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("prescriptionFileUrls");
      if (stored) {
        const parsed = JSON.parse(stored);
        Object.entries(parsed).forEach(([orderId, fileData]: [string, any]) => {
          if (fileData && fileData.mimeType && fileData.dataUrl) {
            // Recreate object URL from the stored data URL
            fetch(fileData.dataUrl)
              .then((res) => res.blob())
              .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                localFileUrlsRef.current[orderId] = {
                  url,
                  type: fileData.mimeType,
                };
              })
              .catch((err) =>
                console.error("Failed to restore file URL:", err),
              );
          }
        });
      }
    } catch (err) {
      console.error(
        "Error restoring prescription files from sessionStorage:",
        err,
      );
    }
  }, []);

  useEffect(() => {
    fetchMedications();

    // loadResidents will now be triggered on handleOpenDialog for optimization

  }, [currentTenantId, fetchMedications]);

  // Reset page when search term, status filter or tenant updates
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, currentTenantId]);

  const handleDownloadMAR = () => {
    navigate("/download-mar");
  };

  const fetchResidentMasterMedications = useCallback(
    async (residentId: string) => {
      try {
        const response = await apiClient.get(
          `/v1/EMR-medications?residentId=${residentId}&isDiscontinued=false`,
        );
        // Handle potential different response shapes (array vs {content: []})
        const responseData = Array.isArray(response.data)
          ? response.data
          : response.data?.content || [];

        // Strict client-side filter to ensure isolation
        const targetId = String(residentId).trim();
        const filtered = responseData.filter((med: any) => {
          const medResidentId = med.residentId || med.resident_id;
          const matchResident = medResidentId && String(medResidentId).trim() === targetId;
          const isDisc =
            med.isDiscontinued === true ||
            med.isDiscontinued === "true" ||
            med.discontinued === true ||
            med.status === "DISCONTINUED";
          return matchResident && !isDisc;
        });

        setResidentMasterMedications(filtered);
      } catch (err) {
        console.error("Failed to fetch resident medications:", err);
        setResidentMasterMedications([]);
      }
    },
    [],
  );

  const loadResidents = async () => {
    if (!currentTenantId) return;
    if (residents.length > 0) return; // Already loaded

    setResidentsLoading(true);
    try {
      const response = await residentService.getActiveLookup(currentTenantId);
      setResidents(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("Failed to load residents:", err);
    } finally {
      setResidentsLoading(false);
    }
  };

  const handleOpenDialog = () => {
    loadResidents();
    // Fetch frequency options if not already loaded
    if (frequencyOptions.length === 0) {
      masterDataService.getActiveLookupValuesByName('Schedule')
        .then(setFrequencyOptions)
        .catch(err => console.error('Failed to load frequency lookup options', err));
    }
    // Fetch route options if not already loaded
    if (routeOptions.length === 0) {
      masterDataService.getActiveLookupValuesByName('Route')
        .then(setRouteOptions)
        .catch(err => console.error('Failed to load route lookup options', err));
    }

    reset({
      medicationItems: [],
      prescriptionFile: null,
      nextRefillDate: null,
    });
    lastAutoFilledQtyRef.current = "";
    setNewMedItem({
      medicationName: "",
      dosage: "",
      route: "",
      frequency: "",
      startDate: new Date(),
      endDate: null,
      medicationExpirationDate: null,
      note: "",
      quantity: "",
      refillsAuthorized: 0,
    });
    setResidentMasterMedications([]);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset();
  };

  // Backend expects LocalDateTime (yyyy-MM-dd'T'HH:mm:ss)
  const formatLocalDateTime = (date?: Date | null) => {
    if (!date) return undefined;
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const onSubmitOrder = async (data: OrderFormData) => {
    try {
      // Clear any previous success message when creating a new order
      setSuccessMessage(null);
      const onError = (errors: any) => {
        console.error("Form validation errors:", errors);
        const errorList: string[] = [];
        if (errors.residentId) errorList.push(`Resident: ${errors.residentId.message}`);
        if (errors.prescribingPhysician) errorList.push(`Physician: ${errors.prescribingPhysician.message}`);
        if (errors.pharmacyName) errorList.push(`Pharmacy Name: ${errors.pharmacyName.message}`);
        if (errors.pharmacyEmail) errorList.push(`Pharmacy Email: ${errors.pharmacyEmail.message}`);
        if (errors.prescriptionNumber) errorList.push(`Prescription No: ${errors.prescriptionNumber.message}`);
        if (errors.medicationItems) {
          if (Array.isArray(errors.medicationItems)) {
            errors.medicationItems.forEach((itemErr: any, i: number) => {
              if (itemErr) {
                Object.values(itemErr).forEach((e: any) => {
                  if (e && e.message) errorList.push(`Medication ${i + 1}: ${e.message}`);
                });
              }
            });
          } else if (errors.medicationItems.message) {
            errorList.push(`Medications: ${errors.medicationItems.message}`);
          }
        }
        setError(`Please fix the following errors: ${errorList.join(", ")}`);
      };

      setLoadingAction({ id: null, type: "CREATE" });
      const now = new Date();

      // Validate that all medications have valid routes (after normalization)
      const invalidMeds =
        data.medicationItems?.filter((item) => {
          const normalizedRoute = normalizeRoute(String(item.route || ""));
          return !normalizedRoute || !VALID_ROUTES.includes(normalizedRoute as string);
        }) || [];
      if (invalidMeds.length > 0) {
        setError("One or more medications have invalid or missing routes. Please review and correct.");
        setLoadingAction(null);
        return;
      }

      // Prepare the order data JSON
      const orderData = {
        residentId: data.residentId,
        prescribingPhysicianName: data.prescribingPhysician,
        pharmacyName: data.pharmacyName,
        pharmacyEmail: data.pharmacyEmail,
        pharmacyContactNumber: data.pharmacyContactNumber,
        prescriptionNumber: data.prescriptionNumber,
        endDate: formatLocalDateTime(data.nextRefillDate),
        medicationItems:
          data.medicationItems?.map((item) => {
            const normalizedRoute =
              normalizeRoute(String(item.route || "")) ||
              (item.route as MedicationRoute);

            return {
              medicationName: item.medicationName,
              dosage: item.dosage,
              route: normalizedRoute as MedicationRoute,
              frequency: item.frequency,
              startDate:
                formatLocalDateTime(item.startDate) || formatLocalDateTime(now),
              endDate: formatLocalDateTime(item.endDate),
              medicationExpirationDate: formatLocalDateTime(
                item.medicationExpirationDate,
              ),
              specialInstructions: `${item.frequency} via ${normalizedRoute}`,
              // Include fields user wanted removed from UI, but might be mandatory in backend
              genericName: "",
              indication: "",
              notes: "",
              isPrn: false,
              quantity: item.quantity ? String(item.quantity) : "",
              refillsAuthorized: typeof item.refillsAuthorized === "number" ? item.refillsAuthorized : 0,
              residentId: data.residentId,
              tenantId: currentTenantId || undefined,
              facilityId: currentTenantId || undefined,
            };
          }) || [],
        facilityId: currentTenantId || undefined,
        tenantId: currentTenantId || undefined, // Explicitly add tenantId as requested by SQL error
        orderedBy: currentUser?.id,
        notes: "", // Send empty notes if removed from UI
      };

      // Create FormData for multipart submission
      const formData = new FormData();

      // Add the order as a JSON blob
      formData.append(
        "order",
        new Blob([JSON.stringify(orderData)], { type: "application/json" }),
      );

      // Add the file if present
      if (data.prescriptionFile) {
        formData.append("file", data.prescriptionFile);
      }

      const created = await OrderMedicationService.createOrder(formData);

      // If a top-level prescription file was uploaded, create a local object URL so it can be previewed immediately
      if (data.prescriptionFile && created && created.id) {
        try {
          const f = data.prescriptionFile as File;
          const url = window.URL.createObjectURL(f);
          localFileUrlsRef.current[created.id] = { url, type: f.type };

          // Also store in sessionStorage for persistence across page refreshes
          const reader = new FileReader();
          reader.onload = () => {
            try {
              if (created.id) {
                const stored = JSON.parse(
                  sessionStorage.getItem("prescriptionFileUrls") || "{}",
                );
                stored[created.id] = {
                  mimeType: f.type,
                  dataUrl: reader.result,
                };
                sessionStorage.setItem(
                  "prescriptionFileUrls",
                  JSON.stringify(stored),
                );
              }
            } catch (err) {
              console.error("Failed to store file in sessionStorage:", err);
            }
          };
          reader.readAsDataURL(f);
        } catch (e) {
          // ignore
        }
      }

      handleCloseDialog();
      await fetchMedications(true); // Silent refresh list
    } catch (err: any) {
      setError(err.message || "Failed to create medication order");
      console.error("Failed to create medication order:", err);
    } finally {
      setLoadingAction(null);
    }
  };

  // Note: Intentionally NOT revoking local object URLs on unmount.
  // This keeps uploaded prescription files visible throughout the session.
  // Object URLs will be garbage collected when the browser tab is closed.

  const handleSendToPharmacy = async (id: string) => {
    setSignatureOrderId(id);
    setSignatureModalOpen(true);
  };

  const handleSignatureSave = async (signatureData: string) => {
    if (!signatureOrderId) return;
    const targetOrderId = signatureOrderId;
    try {
      setSigningLoading(true);

      // 1. Send order & signature to pharmacy
      await OrderMedicationService.sendToPharmacy(
        targetOrderId,
        signatureData,
      );

      // 2. Automatically update backend order status to ORDER_MAIL_SENT
      // This automatically enables the truck icon (Receive Stock)
      let statusUpdateFailed = false;
      try {
        await OrderMedicationService.updateOrderStatus(
          targetOrderId,
          MedicationOrderStatus.ORDER_MAIL_SENT,
        );
      } catch (statusErr) {
        console.warn("Failed to update status to ORDER_MAIL_SENT on backend:", statusErr);
        statusUpdateFailed = true;
      }

      if (statusUpdateFailed) {
        // Stop showing "Sent to pharmacy" when the status wasn't saved on the server
        setError("Email sent, but the status could not be updated – please refresh");
        await fetchMedications(true);
      } else {
        // 3. Update local medications state only after the status call succeeds
        setMedications((prevMeds) =>
          prevMeds.map((m) =>
            m.id === targetOrderId
              ? { ...m, status: MedicationOrderStatus.ORDER_MAIL_SENT }
              : m,
          ),
        );
        setFetchedOrderDetails((prev) =>
          prev && prev.id === targetOrderId
            ? { ...prev, status: MedicationOrderStatus.ORDER_MAIL_SENT }
            : prev,
        );

        // Show inline green success message instead of browser alert
        setSuccessMessage("Order sent to pharmacy successfully");

        // 4. Silent refresh from backend to ensure data consistency
        await fetchMedications(true);
      }
    } catch (err: any) {
      setError(err.message || "Failed to send to pharmacy");
      console.error("Failed to send to pharmacy:", err);
      throw err;
    } finally {
      setSigningLoading(false);
      setSignatureOrderId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleteConfirmationId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmationId) return;
    try {
      setLoadingAction({ id: deleteConfirmationId, type: "DELETE" });
      await OrderMedicationService.deleteOrder(deleteConfirmationId);
      await fetchMedications(true); // Silent refresh
    } catch (err: any) {
      setError(err.message || "Failed to delete medication order");
      console.error("Failed to delete medication order:", err);
    } finally {
      setLoadingAction(null);
      setDeleteConfirmationId(null);
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    const currentMed = medications.find((m) => m.id === id);
    if (
      currentMed?.status === "PARTIALLY_RECEIVED" ||
      currentMed?.status === "RECEIVED"
    ) {
      setError("Partially Received or Received orders cannot be manually changed.");
      return;
    }

    try {
      // Optimistically update local state to reflect change immediately
      setMedications((prevMeds) =>
        prevMeds.map((m) =>
          m.id === id ? { ...m, status: newStatus as any } : m,
        ),
      );

      // Perform API update
      await OrderMedicationService.updateOrderStatus(id, newStatus as any);

      // Silent refresh to ensure data consistency without wiping UI
      await fetchMedications(true);
    } catch (err: any) {
      setError(err.message || "Failed to update medication status");
      console.error("Failed to update medication status:", err);
      // Revert optimization on error (optional, but good practice)
      await fetchMedications(true);
    }
  };

  // Pagination Logic
  const dataToDisplay = filteredMedications;
  const totalPages = Math.ceil(dataToDisplay.length / rowsPerPage);
  const displayedMedications = dataToDisplay.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  // Determine which object to show in the details dialog
  // Prefer the freshly fetched details if available and matching the ID
  const cachedViewingMed = viewingId
    ? dataToDisplay.find((m) => m.id === viewingId)
    : null;
  const viewingMed =
    fetchedOrderDetails && fetchedOrderDetails.id === viewingId
      ? fetchedOrderDetails
      : cachedViewingMed;

  const viewingResident = viewingMed
    ? residents.find((r) => r.id === viewingMed.residentId)
    : null;

  // Fetch details on view
  useEffect(() => {
    if (viewingId && cachedViewingMed && cachedViewingMed.residentId) {
      setFetchingDetails(true);
      setFetchedOrderDetails(null);
      // User requested to integrate strictly on the resident endpoint
      // So we fetch orders for the resident and find the specific one
      // This ensures we get the full payload including Base64 data if the list endpoint omits it
      OrderMedicationService.getOrdersByResident(cachedViewingMed.residentId)
        .then((orders) => {
          const found = orders.find((o) => o.id === viewingId);
          if (found) {
            setFetchedOrderDetails(found);
          }
        })
        .catch((err) =>
          console.error("Failed to fetch detailed order info:", err),
        )
        .finally(() => setFetchingDetails(false));
    } else {
      setFetchedOrderDetails(null);
    }
  }, [viewingId, cachedViewingMed]);

  // Calculate stats using fullMedicationsCache so they represent the whole facility
  const activeCount = fullMedicationsCache.filter((m) => m.status === "ACTIVE").length;
  const discontinuedCount = fullMedicationsCache.filter(
    (m) => m.status === "CANCELLED",
  ).length;

  // Function to pass to ResidentSidebar for searching
  const searchResidents = useCallback((term: string) => {
    console.log("ResidentSidebar search term:", term);
  }, []);

  if (loading && medications.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", px: 0, pb: 2, pt: 3, }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: 2, sm: 0 },
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ typography: 'h1', display: 'flex', alignItems: 'center' }}>
            <MedicalServicesIcon sx={{ fontSize: 36, color: "#48CAE4" }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="700" sx={{ mb: 0.5, color: '#1A2B45' }}>
              Medication Orders
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.85rem" }}>
              Manage medication refills, pharmacy orders, and prescriptions efficiently
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, width: { xs: "100%", sm: "auto" } }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<ShoppingCartIcon />}
            onClick={handleOpenDialog}
            sx={{
              borderRadius: "12px",
              px: 2.5,
              py: 1,
              fontWeight: "normal",
              textTransform: "none",
              boxShadow: "none",
              backgroundColor: "#48CAE4",
              color: "#000000",
              "&:hover": {
                backgroundColor: "#3ab3cc",
                boxShadow: "none",
              },
            }}
          >
            Refill Medication
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadMAR}
            sx={{
              borderRadius: "12px",
              px: 2.5,
              py: 1,
              fontWeight: "normal",
              textTransform: "none",
              boxShadow: "none",
              backgroundColor: "#48CAE4",
              color: "#000000",
              "&:hover": {
                backgroundColor: "#3ab3cc",
                boxShadow: "none",
              },
            }}
          >
            Download MAR
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Stats Cards — same UI pattern as Dashboard StatCards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Total Orders */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            elevation={0}
            onClick={() => setStatusFilter("ALL")}
            sx={{
              p: 2.5,
              borderRadius: '16px',
              border: '1px solid',
              borderColor: 'divider',
              background: '#fff',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease-in-out',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: `0 4px 16px ${alpha('#00B4D8', 0.15)}`,
                borderColor: alpha('#00B4D8', 0.5),
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.25, borderRadius: '12px', background: '#00B4D820', color: '#00B4D8', display: 'flex' }}>
                <MedicalServicesIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#00B4D8', lineHeight: 1, fontSize: "1.75rem" }}>
                  {fullMedicationsCache.length}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: "0.82rem", whiteSpace: "nowrap" }}>
                  Total Orders
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Active Orders */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            elevation={0}
            onClick={() => setStatusFilter("ACTIVE")}
            sx={{
              p: 2.5,
              borderRadius: '16px',
              border: '1px solid',
              borderColor: 'divider',
              background: '#fff',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease-in-out',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: `0 4px 16px ${alpha('#34d399', 0.15)}`,
                borderColor: alpha('#34d399', 0.5),
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.25, borderRadius: '12px', background: '#34d39920', color: '#34d399', display: 'flex' }}>
                <CheckCircleIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#34d399', lineHeight: 1, fontSize: "1.75rem" }}>
                  {activeCount}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: "0.82rem", whiteSpace: "nowrap" }}>
                  Active Orders
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Cancelled Orders */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            elevation={0}
            onClick={() => setStatusFilter("CANCELLED")}
            sx={{
              p: 2.5,
              borderRadius: '16px',
              border: '1px solid',
              borderColor: 'divider',
              background: '#fff',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease-in-out',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: `0 4px 16px ${alpha('#a78bfa', 0.15)}`,
                borderColor: alpha('#a78bfa', 0.5),
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.25, borderRadius: '12px', background: '#a78bfa20', color: '#a78bfa', display: 'flex' }}>
                <CancelIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#a78bfa', lineHeight: 1, fontSize: "1.75rem" }}>
                  {discontinuedCount}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: "0.82rem", whiteSpace: "nowrap" }}>
                  Cancelled
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>



      {/* Search Bar and Status Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            fullWidth
            placeholder="Search By Resident Name, Primary Care Provider, Pharmacy Name, Pharmacy Email, or Status"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Status</MenuItem>
              {Object.values(MedicationOrderStatus).map((status) => (
                <MenuItem key={status} value={status}>
                  {formatStatus(status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Reset all filters">
            <IconButton
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("ALL");
                setPage(1);
              }}
              size="medium"
              sx={{
                flexShrink: 0,
                color: 'text.secondary',
                transition: 'color 0.2s',
                '&:hover': {
                  color: 'error.main',
                  bgcolor: 'transparent',
                },
              }}
            >
              <ResetIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {searchLoading && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="caption" color="textSecondary">
              Searching...
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Orders Table */}
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 1300 }}>
            <TableHead sx={{
              "& .MuiTableCell-head": {
                color: "#434445ff !important",
              }
            }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>
                  Resident Name
                </TableCell>
                {/* <TableCell sx={{ fontWeight: 'bold' }}>Medications</TableCell> */}
                <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                  Primary Care Provider
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                  Prescription Number
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>
                  Pharmacy Name
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>
                  Pharmacy Email
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>
                  Pharmacy Contact Number
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                  Created At
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                  Created By
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                  Updated At
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                  Updated By
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 160 }}>
                  Received Stock
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 160 }}>
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    textAlign: "center",
                    minWidth: 180,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : displayedMedications.length > 0 ? (
                displayedMedications.map((med) => {
                  const resident = residents.find(
                    (r) => r.id === med.residentId,
                  );
                  return (
                    <TableRow key={med.id}>
                      <TableCell sx={{ wordBreak: "break-word" }}>
                        {med.residentFullName || (resident
                          ? `${resident.firstName} ${resident.lastName}`
                          : med.residentId)}
                      </TableCell>
                      {/* <TableCell sx={{ fontWeight: 500 }}>
                      {med.medicationItems?.map(item => item.medicationName).join(', ')}
                      {med.medicationItems && med.medicationItems.length > 3 && '...'}
                    </TableCell> */}
                      <TableCell sx={{ wordBreak: "break-word" }}>
                        {med.prescribingPhysicianName || "N/A"}
                      </TableCell>
                      <TableCell sx={{ wordBreak: "break-word" }}>
                        {med.prescriptionNumber || "-"}
                      </TableCell>
                      <TableCell>
                        {med.pharmacyName &&
                          (med.pharmacyName.length > 25 ||
                            /\S{15,}/.test(med.pharmacyName)) ? (
                          <Box
                            sx={{
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                              maxWidth: 300,
                            }}
                          >
                            {med.pharmacyName}
                          </Box>
                        ) : (
                          med.pharmacyName || "-"
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {med.pharmacyEmail || "-"}
                      </TableCell>
                      <TableCell sx={{ color: "#4B5563" }}>
                        {med.pharmacyContactNumber || "-"}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatToUIDateTimeLong(med.createdAt)}
                      </TableCell>
                      <TableCell>
                        {med.createdByName || med.orderedBy || "-"}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatToUIDateTimeLong(med.updatedAt)}
                      </TableCell>
                      <TableCell>
                        {med.updatedByName || med.updatedBy || "-"}
                      </TableCell>
                      <TableCell>
                        {renderDeliveryProgress(med)}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const isLocked =
                            med.status === "RECEIVED" ||
                            med.status === "PARTIALLY_RECEIVED";

                          return (
                            <Tooltip
                              title={
                                med.status === "PARTIALLY_RECEIVED"
                                  ? "Partially received orders must be completed via Receive Stock"
                                  : med.status === "RECEIVED"
                                  ? "Order is fully received"
                                  : ""
                              }
                              arrow
                            >
                              <span>
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                  <Select
                                    value={med.status}
                                    disabled={isLocked}
                                    readOnly={isLocked}
                                    onChange={(e) =>
                                      med.id &&
                                      handleStatusChange(med.id, e.target.value)
                                    }
                                    renderValue={(selected) =>
                                      formatStatus(selected as string)
                                    }
                                    sx={{
                                      ...(isLocked && {
                                        cursor: "default",
                                        bgcolor: "#F8FAFC",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "#CBD5E1 !important",
                                        },
                                        "& .MuiSelect-select": {
                                          color: "#1E293B",
                                          WebkitTextFillColor: "#1E293B !important",
                                          cursor: "default",
                                        },
                                        "& .MuiSelect-icon": {
                                          color: "#94A3B8",
                                          display: "none",
                                        },
                                      }),
                                    }}
                                  >
                                    {(() => {
                                      if (med.status === "PENDING") {
                                        return [
                                          <MenuItem key="ACTIVE" value="ACTIVE">Active</MenuItem>,
                                          <MenuItem key="PENDING" value="PENDING">Pending Delivery</MenuItem>,
                                          <MenuItem key="CANCELLED" value="CANCELLED">Cancelled</MenuItem>,
                                        ];
                                      }

                                      if (med.status === "ORDER_MAIL_SENT") {
                                        return [
                                          <MenuItem key="ORDER_MAIL_SENT" value="ORDER_MAIL_SENT">Order Mail Sent</MenuItem>,
                                          <MenuItem key="ACTIVE" value="ACTIVE">Active</MenuItem>,
                                          <MenuItem key="CANCELLED" value="CANCELLED">Cancelled</MenuItem>,
                                        ];
                                      }

                                      if (med.status === "ACTIVE") {
                                        return [
                                          <MenuItem key="ACTIVE" value="ACTIVE">Active</MenuItem>,
                                          <MenuItem key="PENDING" value="PENDING">Pending Delivery</MenuItem>,
                                          <MenuItem key="CANCELLED" value="CANCELLED">Cancelled</MenuItem>,
                                        ];
                                      }

                                      return [
                                        <MenuItem key="ACTIVE" value="ACTIVE">Active</MenuItem>,
                                        <MenuItem key="PENDING" value="PENDING">Pending Delivery</MenuItem>,
                                        med.status === "PARTIALLY_RECEIVED" ? (
                                          <MenuItem key="PARTIALLY_RECEIVED" value="PARTIALLY_RECEIVED" sx={{ display: "none" }}>
                                            Partially Received
                                          </MenuItem>
                                        ) : null,
                                        med.status === "RECEIVED" ? (
                                          <MenuItem key="RECEIVED" value="RECEIVED" sx={{ display: "none" }}>
                                            Received
                                          </MenuItem>
                                        ) : null,
                                        <MenuItem key="CANCELLED" value="CANCELLED">Cancelled</MenuItem>,
                                      ].filter(Boolean);
                                    })()}
                                  </Select>
                                </FormControl>
                              </span>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexWrap: "nowrap",
                            gap: 0.5,
                          }}
                        >
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => setViewingId(med.id || null)}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {(() => {
                            const statusUpper = String(med.status || "").toUpperCase();
                            const isFullyReceived = statusUpper === "RECEIVED";
                            const isCancelled = ["CANCELLED", "DISCONTINUED"].includes(statusUpper);
                            const canReceive = ["ORDER_MAIL_SENT", "PARTIALLY_RECEIVED"].includes(statusUpper);

                            return (
                              <Tooltip
                                title={
                                  isFullyReceived
                                    ? "Order Fully Received"
                                    : isCancelled
                                    ? "Order Cancelled"
                                    : !canReceive
                                    ? "Please send email to pharmacy first"
                                    : "Receive Stock"
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleOpenReceiveModal(med)}
                                    disabled={!canReceive}
                                  >
                                    <LocalShippingIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            );
                          })()}

                          <Tooltip
                            title={
                              printingOrderId === med.id
                                ? "Cancel Printing"
                                : "Print"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color={
                                  printingOrderId === med.id
                                    ? "secondary"
                                    : "primary"
                                }
                                onClick={() =>
                                  med.id && handlePrintOrder(med.id)
                                }
                              >
                                {printingOrderId === med.id ? (
                                  <Box
                                    sx={{
                                      position: "relative",
                                      display: "inline-flex",
                                    }}
                                  >
                                    <CircularProgress size={16} />
                                    <CloseIcon
                                      sx={{
                                        position: "absolute",
                                        fontSize: "8px",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <PrintIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Send to Pharmacy">
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => med.id && handleSendToPharmacy(med.id)}
                                disabled={["PENDING", "RECEIVED", "CANCELLED"].includes(med.status as string)}
                              >
                                <MailIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => med.id && handleDelete(med.id)}
                              disabled={
                                loadingAction != null &&
                                (loadingAction.id === med.id ||
                                  loadingAction.type === "CREATE")
                              }
                            >
                              {loadingAction?.id === med.id &&
                                loadingAction?.type === "DELETE" ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" color="textSecondary">
                      No medication orders found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {displayedMedications.length} of {dataToDisplay.length}{" "}
          entries
        </Typography>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, value) => setPage(value)}
          variant="outlined"
          shape="rounded"
          size="small"
        />
      </Box>

      {/* Create Order Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Create New Medication Order
        </DialogTitle>
        <DialogContent dividers>
          <form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="residentId"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={sortedResidents.filter(r => r?.status?.toUpperCase() === "ACTIVE")}
                      loading={residentsLoading}
                      getOptionLabel={(option: ResidentDTO) => {
                        const name = (option as any).residentName || (option as any).fullName || `${option.firstName || ''} ${option.lastName || ''}`.trim();
                        const roomBed = option.roomNumber ? ` (Room: ${option.roomNumber}${option.bedNumber ? `, Bed: ${option.bedNumber}` : ""})` : "";
                        return `${name}${roomBed}` || "Unknown Resident";
                      }}
                      isOptionEqualToValue={(option, value) => option.id === (typeof value === 'string' ? value : value?.id)}
                      value={sortedResidents.find(r => r.id === field.value) || null}
                      onChange={(_, newValue: ResidentDTO | null) => {
                        const residentId = newValue?.id || "";
                        field.onChange(residentId);

                        // Clear medications and reset inputs when resident changes
                        setValue("medicationItems", []);
                        setNewMedItem({
                          medicationName: "",
                          dosage: "",
                          route: "",
                          frequency: "",
                          startDate: new Date(),
                          endDate: null,
                          medicationExpirationDate: null,
                          note: "",
                        });

                        // Auto-populate Prescribing Physician from resident's attending physician.
                        // The active-lookup endpoint may return a lightweight payload, so we
                        // check multiple field aliases and fall back to a full resident fetch.
                        const physicianFromLookup =
                          (newValue as any)?.attendingPhysicianName ||
                          (newValue as any)?.attendingPhysician ||
                          "";
                        setValue("prescribingPhysician", physicianFromLookup);

                        if (!physicianFromLookup && residentId) {
                          residentService.getResidentById(residentId)
                            .then((fullResident) => {
                              const name =
                                fullResident?.attendingPhysicianName ||
                                (fullResident as any)?.attendingPhysician ||
                                "";
                              if (name) setValue("prescribingPhysician", name);
                            })
                            .catch(() => {/* silently ignore if fetch fails */ });
                        }
                        if (residentId) {
                          fetchResidentMasterMedications(residentId);
                        } else {
                          setResidentMasterMedications([]);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={<span>Select Resident <span style={{ color: '#d32f2f' }}>*</span></span>}
                          error={!!errors.residentId}
                          helperText={errors.residentId?.message}
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {residentsLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="prescribingPhysician"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={<span>Primary Care Provider <span style={{ color: '#d32f2f' }}>*</span></span>}
                      fullWidth
                      error={!!errors.prescribingPhysician}
                      InputLabelProps={{
                        shrink: !!field.value ? true : undefined,
                      }}
                      helperText={errors.prescribingPhysician?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="prescriptionNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Prescription Number"
                      fullWidth
                      error={!!errors.prescriptionNumber}
                      helperText={errors.prescriptionNumber?.message}
                      inputProps={{ maxLength: 10 }}
                      InputLabelProps={{
                        shrink:
                          field.value !== undefined &&
                          field.value !== null &&
                          field.value !== "",
                      }}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (value.length <= 10) {
                          field.onChange(value);
                        }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="pharmacyName"
                  control={control}
                  rules={{
                    required: "Pharmacy name is required",
                    pattern: {
                      value: /^[A-Za-z\s]+$/,
                      message: "Only alphabet characters are allowed",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={<span>Pharmacy Name <span style={{ color: '#d32f2f' }}>*</span></span>}
                      fullWidth
                      InputLabelProps={{
                        shrink:
                          field.value !== undefined &&
                          field.value !== null &&
                          field.value !== "",
                      }}
                      onChange={(e) => {
                        const value = e.target.value.replace(
                          /[^A-Za-z\s]/g,
                          "",
                        );
                        field.onChange(value);
                      }}
                      error={!!errors.pharmacyName}
                      helperText={errors.pharmacyName?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="pharmacyEmail"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={<span>Pharmacy Email <span style={{ color: '#d32f2f' }}>*</span></span>}
                      fullWidth
                      error={!!errors.pharmacyEmail}
                      helperText={errors.pharmacyEmail?.message}
                      InputLabelProps={{
                        shrink:
                          field.value !== undefined &&
                          field.value !== null &&
                          field.value !== "",
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="pharmacyContactNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={<span>Pharmacy Contact Number <span style={{ color: '#d32f2f' }}>*</span></span>}
                      fullWidth
                      error={!!errors.pharmacyContactNumber}
                      helperText={errors.pharmacyContactNumber?.message}
                      InputLabelProps={{
                        shrink:
                          field.value !== undefined &&
                          field.value !== null &&
                          field.value !== "",
                      }}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        if (value.length <= 10) {
                          field.onChange(value);
                        }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 0.5,
                    fontWeight: "bold",
                    color: "rgba(0, 0, 0, 0.6)",
                  }}
                >
                  Upload Prescription
                </Typography>
                <Box sx={{ position: "relative", width: "100%" }}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      justifyContent: "start",
                      textTransform: "none",
                      height: "56px",
                      borderColor: "rgba(0, 0, 0, 0.23)",
                      color: watch("prescriptionFile")
                        ? "text.primary"
                        : "text.secondary",
                      "&:hover": {
                        borderColor: "text.primary",
                      },
                      pr: watch("prescriptionFile") ? 6 : 2,
                    }}
                  >
                    <Box
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                    >
                      {watch("prescriptionFile")?.name || "Choose File"}
                    </Box>
                    <input
                      type="file"
                      hidden
                      accept="image/*,.pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setValue("prescriptionFile", file, { shouldValidate: true });
                        e.target.value = "";
                      }}
                    />
                  </Button>
                  {watch("prescriptionFile") && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setValue("prescriptionFile", null, { shouldValidate: true });
                      }}
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 2,
                        color: "error.main",
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {errors.prescriptionFile && (
                  <FormHelperText error sx={{ mx: 0, mt: 1 }}>
                    {errors.prescriptionFile.message}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 0.5,
                    fontWeight: "bold",
                    color: "rgba(0, 0, 0, 0.6)",
                  }}
                >
                  Next Order / Refill Date
                </Typography>
                <Controller
                  name="nextRefillDate"
                  control={control}
                  render={({ field }) => (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        {...field}
                        format="dd/MM/yyyy"
                        onChange={(date) => {
                          field.onChange(date);
                          if (date) {
                            const autoQty = calculateMedicationQuantity(
                              newMedItem.startDate,
                              date,
                              newMedItem.frequency,
                            );
                            const next = getNextQuantityOnFieldChange(
                              newMedItem.quantity,
                              lastAutoFilledQtyRef.current,
                              autoQty,
                            );
                            lastAutoFilledQtyRef.current = next.lastAutoQuantity;
                            setNewMedItem((prev: MedicationEntry) => ({
                              ...prev,
                              endDate: date,
                              quantity: next.quantity,
                            }));
                          }
                        }}
                        sx={{ width: "100%" }}
                        slotProps={{
                          textField: {
                            size: "medium",
                            fullWidth: true,
                            error: !!errors.nextRefillDate,
                            helperText: errors.nextRefillDate?.message,
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>Medications</Divider>
              </Grid>

              <Grid item xs={12}>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    overflowX: "auto",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead sx={{
                      "& .MuiTableCell-head": {
                        color: "#434445ff !important",
                        fontSize: "1rem !important",
                        fontWeight: "600 !important"
                      }
                    }}>
                      <TableRow
                        sx={{
                          "& th": {
                            backgroundColor: "#fdfdfd",
                            fontWeight: "bold",
                            color: "#546E7A",
                            fontSize: "0.7rem",
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                            letterSpacing: "0.05em",
                          },
                        }}
                      >
                        <TableCell sx={{ minWidth: 180 }}>
                          Medication Name <span style={{ color: "#d32f2f" }}>*</span>
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>Dosage <span style={{ color: "#d32f2f" }}>*</span></TableCell>
                        <TableCell sx={{ minWidth: 200 }}>Frequency <span style={{ color: "#d32f2f" }}>*</span></TableCell>
                        <TableCell sx={{ minWidth: 200 }}>Route <span style={{ color: "#d32f2f" }}>*</span></TableCell>
                        <TableCell sx={{ minWidth: 180 }}>Start Date <span style={{ color: "#d32f2f" }}>*</span></TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          Refill Date
                        </TableCell>
                        <TableCell sx={{ minWidth: 110 }}>
                          Quantity
                        </TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          Medication Expiration Date
                        </TableCell>
                        <TableCell align="center" sx={{ minWidth: 100 }}>
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.map((field, index) => (
                        <TableRow
                          key={field.id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>
                            {watch(`medicationItems.${index}.medicationName`)}
                          </TableCell>
                          <TableCell sx={{ color: "#607D8B" }}>
                            {watch(`medicationItems.${index}.dosage`)}
                          </TableCell>
                          <TableCell sx={{ color: "#607D8B" }}>
                            {watch(`medicationItems.${index}.frequency`) || "-"}
                          </TableCell>
                          <TableCell sx={{ color: "#607D8B" }}>
                            {watch(`medicationItems.${index}.route`) || ""}
                          </TableCell>
                          <TableCell sx={{ color: "#607D8B" }}>
                            {formatToUIDate(watch(`medicationItems.${index}.startDate`))}
                          </TableCell>
                          <TableCell sx={{ color: "#607D8B" }}>
                            {formatToUIDate(watch(`medicationItems.${index}.endDate`)) || "N/A"}
                          </TableCell>
                          <TableCell sx={{ color: "#607D8B" }}>
                            {watch(`medicationItems.${index}.quantity`) || "-"}
                          </TableCell>
                          <TableCell sx={{ color: "#607D8B" }}>
                            {formatToUIDate(watch(`medicationItems.${index}.medicationExpirationDate`)) || "N/A"}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => remove(index)}
                              sx={{ "&:hover": { backgroundColor: "#ffebee" } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* Entry Row */}
                      <TableRow
                        sx={{
                          backgroundColor: "#fafafa",
                          "& td": { borderBottom: "none" },
                        }}
                      >
                        <TableCell sx={{ py: 1.5 }}>
                          <Autocomplete
                            forcePopupIcon={!!residentId}
                            size="small"
                            options={availableMedicationOptions}
                            value={newMedItem.medicationName}
                            onChange={(event, value) => {
                              const selectedVal = value as string;
                              setNewMedItem((prev: MedicationEntry) => ({
                                ...prev,
                                medicationName: selectedVal || "",
                              }));
                              setNewMedItemErrors((prev) => {
                                const next = { ...prev };
                                delete next.medicationName;
                                return next;
                              });
                              setError(null);
                              if (selectedVal) {
                                const masterMed =
                                  residentMasterMedications.find(
                                    (m) => m.medicationName === selectedVal,
                                  );
                                if (masterMed) {
                                  const masterFreq = masterMed.schedule || masterMed.frequency || "";
                                  const autoQty = calculateMedicationQuantity(
                                    newMedItem.startDate,
                                    newMedItem.endDate,
                                    masterFreq,
                                  );
                                  const next = getNextQuantityOnFieldChange(
                                    newMedItem.quantity,
                                    lastAutoFilledQtyRef.current,
                                    autoQty,
                                  );
                                  lastAutoFilledQtyRef.current = next.lastAutoQuantity;

                                  setNewMedItem((prev: MedicationEntry) => ({
                                    ...prev,
                                    dosage: masterMed.dosage || "",
                                    frequency: masterFreq,
                                    note: "",
                                    route: normalizeRoute(masterMed.route),
                                    quantity: next.quantity,
                                  }));
                                }
                              } else {
                                // Clear all fields if medication is cleared
                                setNewMedItem((prev: MedicationEntry) => ({
                                  ...prev,
                                  dosage: "",
                                  frequency: "",
                                  note: "",
                                  route: "",
                                }));
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Medication *"
                                variant="outlined"
                                error={!!newMedItemErrors.medicationName}
                                helperText={newMedItemErrors.medicationName}
                                inputProps={{
                                  ...params.inputProps,
                                  readOnly: true,
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "6px",
                                    backgroundColor: "white",
                                    cursor: "pointer",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: newMedItemErrors.medicationName ? "#d32f2f" : "#0066cc",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                      borderColor: newMedItemErrors.medicationName ? "#d32f2f" : "#0066cc",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                      borderColor: newMedItemErrors.medicationName ? "#d32f2f" : "#0066cc",
                                    },
                                  },
                                  "& input": { cursor: "pointer" },
                                }}
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            placeholder="Dose *"
                            value={newMedItem.dosage}
                            error={!!newMedItemErrors.dosage}
                            helperText={newMedItemErrors.dosage}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val.trim().startsWith("-") || val.includes("--")) return;
                              setNewMedItem((prev: MedicationEntry) => ({
                                ...prev,
                                dosage: val,
                              }));
                              setNewMedItemErrors((prev) => {
                                const next = { ...prev };
                                delete next.dosage;
                                return next;
                              });
                              setError(null);
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "6px",
                                backgroundColor: "white",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: newMedItemErrors.dosage ? "#d32f2f" : "#0066cc",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: newMedItemErrors.dosage ? "#d32f2f" : "#0066cc",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: newMedItemErrors.dosage ? "#d32f2f" : "#0066cc",
                                },
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            disableClearable
                            size="small"
                            options={frequencyOptions.length > 0 ? frequencyOptions.map(opt => opt.valueName) : standardFrequencies}
                            value={newMedItem.frequency}
                            onInputChange={(event, newValue) => {
                              const autoQty = calculateMedicationQuantity(
                                newMedItem.startDate,
                                newMedItem.endDate,
                                newValue,
                              );
                              const next = getNextQuantityOnFieldChange(
                                newMedItem.quantity,
                                lastAutoFilledQtyRef.current,
                                autoQty,
                              );
                              lastAutoFilledQtyRef.current = next.lastAutoQuantity;
                              setNewMedItem((prev: MedicationEntry) => ({
                                ...prev,
                                frequency: newValue,
                                quantity: next.quantity,
                              }));
                              setNewMedItemErrors((prev) => {
                                const next = { ...prev };
                                delete next.frequency;
                                return next;
                              });
                              setError(null);
                            }}
                            popupIcon={<ArrowDropDownIcon />}
                            forcePopupIcon={true}
                            slotProps={{
                              popper: {
                                placement: "bottom-start",
                                modifiers: [{ name: "flip", enabled: false }],
                                sx: {
                                  "& .MuiAutocomplete-listbox": {
                                    maxHeight: "150px",
                                    backgroundColor: "white",
                                    padding: 0,
                                    "& .MuiAutocomplete-option": {
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    },
                                  },
                                },
                              },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Frequency *"
                                error={!!newMedItemErrors.frequency}
                                helperText={newMedItemErrors.frequency}
                                onChange={() => {
                                  setNewMedItemErrors((prev) => ({
                                    ...prev,
                                    frequency: undefined,
                                  }));
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "6px",
                                    backgroundColor: "white",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: newMedItemErrors.frequency ? "#d32f2f" : "#0066cc",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                      borderColor: newMedItemErrors.frequency ? "#d32f2f" : "#0066cc",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                      borderColor: newMedItemErrors.frequency ? "#d32f2f" : "#0066cc",
                                    },
                                    // Ensure text is not cut off by dropdown icon
                                    pr: "35px !important",
                                  },
                                }}
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl
                            fullWidth
                            error={!!newMedItemErrors.route}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "6px",
                                backgroundColor: "white",
                              },
                            }}
                          >
                            {(() => {
                              const routeAutocompleteOptions = routeOptions.length > 0
                                ? routeOptions.map(opt => ({ label: opt.valueName, value: normalizeRoute(opt.valueName) || opt.valueName }))
                                : Object.values(MedicationRoute).map(r => ({ label: r, value: r }));

                              return (
                                <Autocomplete
                                  size="small"
                                  fullWidth
                                  disableClearable
                                  options={routeAutocompleteOptions}
                                  getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                                  isOptionEqualToValue={(option, val) => {
                                    const valStr = typeof val === 'string' ? val : val?.value;
                                    return option.value === valStr;
                                  }}
                                  value={routeAutocompleteOptions.find(o => o.value === newMedItem.route) || undefined}
                                  onChange={(event, newValue) => {
                                    const selectedValue = typeof newValue === 'string' ? newValue : newValue?.value;
                                    if (selectedValue) {
                                      setNewMedItem((prev: MedicationEntry) => ({
                                        ...prev,
                                        route: selectedValue as MedicationRoute,
                                      }));
                                      setNewMedItemErrors((prev) => {
                                        const next = { ...prev };
                                        delete next.route;
                                        return next;
                                      });
                                      setError(null);
                                    }
                                  }}
                                  popupIcon={<ArrowDropDownIcon />}
                                  forcePopupIcon={true}
                                  slotProps={{
                                    popper: {
                                      placement: "bottom-start",
                                      modifiers: [{ name: "flip", enabled: false }],
                                      sx: {
                                        "& .MuiAutocomplete-listbox": {
                                          maxHeight: "150px",
                                          backgroundColor: "white",
                                          padding: 0,
                                          "& .MuiAutocomplete-option": {
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          },
                                        },
                                      },
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      placeholder="Select Route *"
                                      error={!!newMedItemErrors.route}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "6px",
                                          backgroundColor: "white",
                                          "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: newMedItemErrors.route ? "#d32f2f" : "#0066cc",
                                          },
                                          "&:hover .MuiOutlinedInput-notchedOutline": {
                                            borderColor: newMedItemErrors.route ? "#d32f2f" : "#0066cc",
                                          },
                                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: newMedItemErrors.route ? "#d32f2f" : "#0066cc",
                                          },
                                          pr: "35px !important",
                                        },
                                      }}
                                    />
                                  )}
                                />
                              );
                            })()}
                            {newMedItemErrors.route && (
                              <FormHelperText>{newMedItemErrors.route}</FormHelperText>
                            )}
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              format="dd/MM/yyyy"
                              disablePast
                              value={newMedItem.startDate}
                              onChange={(v) => {
                                const autoQty = calculateMedicationQuantity(
                                  v,
                                  newMedItem.endDate,
                                  newMedItem.frequency,
                                );
                                const next = getNextQuantityOnFieldChange(
                                  newMedItem.quantity,
                                  lastAutoFilledQtyRef.current,
                                  autoQty,
                                );
                                lastAutoFilledQtyRef.current = next.lastAutoQuantity;
                                setNewMedItem((prev: MedicationEntry) => ({
                                  ...prev,
                                  startDate: v,
                                  quantity: next.quantity,
                                }));
                              }}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  fullWidth: true,
                                  placeholder: "Start Date *",
                                  sx: {
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "6px",
                                      backgroundColor: "white",
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#0066cc",
                                      },
                                      "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#0066cc",
                                      },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#0066cc",
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </TableCell>
                        <TableCell>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              format="dd/MM/yyyy"
                              disablePast
                              value={newMedItem.endDate}
                              onChange={(v) => {
                                const autoQty = calculateMedicationQuantity(
                                  newMedItem.startDate,
                                  v,
                                  newMedItem.frequency,
                                );
                                const next = getNextQuantityOnFieldChange(
                                  newMedItem.quantity,
                                  lastAutoFilledQtyRef.current,
                                  autoQty,
                                );
                                lastAutoFilledQtyRef.current = next.lastAutoQuantity;
                                setNewMedItem((prev: MedicationEntry) => ({
                                  ...prev,
                                  endDate: v,
                                  quantity: next.quantity,
                                }));
                              }}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  fullWidth: true,
                                  placeholder: "Refill Date",
                                  sx: {
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "6px",
                                      backgroundColor: "white",
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#0066cc",
                                      },
                                      "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#0066cc",
                                      },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#0066cc",
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            placeholder="Quantity"
                            type="number"
                            value={newMedItem.quantity || ""}
                            onChange={(e) =>
                              setNewMedItem((prev: MedicationEntry) => ({
                                ...prev,
                                quantity: e.target.value,
                              }))
                            }
                            inputProps={{ min: 0 }}
                            sx={{
                              minWidth: 90,
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "6px",
                                backgroundColor: "white",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#0066cc",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#0066cc",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#0066cc",
                                },
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              format="dd/MM/yyyy"
                              disablePast
                              minDate={newMedItem.endDate || undefined}
                              value={newMedItem.medicationExpirationDate}
                              referenceDate={newMedItem.endDate || undefined}
                              onChange={(v) =>
                                setNewMedItem((prev: MedicationEntry) => ({
                                  ...prev,
                                  medicationExpirationDate: v,
                                }))
                              }
                              slotProps={{
                                textField: {
                                  size: "small",
                                  fullWidth: true,
                                  placeholder: "Medication Expiration Date",
                                  sx: {
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "6px",
                                      backgroundColor: "white",
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#0066cc",
                                      },
                                      "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#0066cc",
                                      },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#0066cc",
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              const rowErrors: {
                                medicationName?: string;
                                dosage?: string;
                                frequency?: string;
                                route?: string;
                              } = {};

                              if (!newMedItem.medicationName) {
                                rowErrors.medicationName = "Medication name is required";
                              }
                              if (!newMedItem.dosage) {
                                rowErrors.dosage = "Dosage is required";
                              }
                              if (!newMedItem.frequency) {
                                rowErrors.frequency = "Frequency is required";
                              }
                              if (!newMedItem.route) {
                                rowErrors.route = "Route is required";
                              }

                              if (Object.keys(rowErrors).length > 0) {
                                setNewMedItemErrors(rowErrors);
                                return;
                              }

                              const normalizedRoute =
                                normalizeRoute(newMedItem.route as string) ||
                                (newMedItem.route as MedicationRoute);

                              append({
                                ...newMedItem,
                                route: normalizedRoute,
                                // Provide defaults for required fields if they are missing
                                endDate: newMedItem.endDate || null,
                                medicationExpirationDate:
                                  newMedItem.medicationExpirationDate || null,
                                quantity: newMedItem.quantity || "",
                                refillsAuthorized: typeof newMedItem.refillsAuthorized === "number" ? newMedItem.refillsAuthorized : 0,
                              });
                              // Reset the entry row after successful append
                              lastAutoFilledQtyRef.current = "";
                              setNewMedItem((prev) => ({
                                medicationName: "",
                                dosage: "",
                                route: "",
                                frequency: "", // Clear frequency
                                startDate: prev.startDate, // Persist Start Date
                                endDate: null,
                                medicationExpirationDate: null,
                                note: "",
                                quantity: "",
                                refillsAuthorized: 0,
                              }));
                              setNewMedItemErrors({});
                              setError(null);
                            }}
                            sx={{
                              borderRadius: '12px',
                              px: 3,
                              py: 1,
                              fontWeight: 'normal',
                              textTransform: 'none',
                              bgcolor: '#48CAE4',
                              color: '#000000',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              '&:hover': {
                                bgcolor: '#3ab3cc',
                                boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
                              }
                            }}
                          >
                            Add
                          </Button>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                {errors.medicationItems && (
                  <FormHelperText error sx={{ mt: 1, ml: 1 }}>
                    {errors.medicationItems.message}
                  </FormHelperText>
                )}
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit(onSubmitOrder, (errors: any) => {
              console.error("Form validation errors:", errors);
            })}
            variant="contained"
            disabled={false}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1,
              fontWeight: 'normal',
              textTransform: 'none',
              bgcolor: '#48CAE4',
              color: '#000000',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              '&:hover': {
                bgcolor: '#3ab3cc',
                boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
              },
              '&.Mui-disabled': {
                bgcolor: '#48CAE4',
                color: 'rgba(0,0,0,0.5)',
                opacity: 0.6
              }
            }}
          >
            {loadingAction?.type === "CREATE" ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Submit Order"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Order Details Dialog */}
      <Dialog
        open={!!viewingId}
        onClose={() => setViewingId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Medication Order Details
        </DialogTitle>
        <DialogContent dividers>
          {fetchingDetails ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            viewingMed && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography
                        variant="subtitle2"
                        color="black"
                        component="div"
                      >
                        <Box sx={{ fontWeight: "bold", display: "inline" }}>
                          Resident Name
                        </Box>
                      </Typography>

                      <Typography variant="body1">
                        {viewingMed.residentFullName || (viewingResident
                          ? `${viewingResident.firstName} ${viewingResident.lastName}`
                          : viewingMed.residentId)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography
                        variant="subtitle2"
                        color="black"
                        component="div"
                      >
                        <Box sx={{ fontWeight: "bold", display: "inline" }}>
                          Primary Care Provider
                        </Box>
                      </Typography>
                      <Typography variant="body1">
                        {viewingMed.prescribingPhysicianName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography
                        variant="subtitle2"
                        color="black"
                        component="div"
                      >
                        <Box sx={{ fontWeight: "bold", display: "inline" }}>
                          Prescription Number
                        </Box>
                      </Typography>
                      <Typography variant="body1">
                        {viewingMed.prescriptionNumber || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography
                        variant="subtitle2"
                        color="black"
                        component="div"
                      >
                        <Box sx={{ fontWeight: "bold", display: "inline" }}>
                          Status:{" "}
                        </Box>
                        {formatStatus(viewingMed.status || "")}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        Medications
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead sx={{
                            "& .MuiTableCell-head": {
                              color: "#434445ff !important",

                            }
                          }}>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Dosage</TableCell>
                              <TableCell>Route</TableCell>
                              <TableCell>Frequency</TableCell>
                              <TableCell>Medication Start Date</TableCell>
                              <TableCell>Refill Date</TableCell>
                              <TableCell align="center">Ordered Qty</TableCell>
                              <TableCell align="center">Received Stock</TableCell>
                              <TableCell align="center">Remaining</TableCell>
                              <TableCell align="center">Live Stock</TableCell>
                              <TableCell>Medication Expiration Date</TableCell>
                              <TableCell>Upload Prescription</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {viewingMed.medicationItems?.map(
                              (item: any, idx: number) => {
                                // Logic to hide the per-medication View button if we have a top-level prescription
                                // Or keep it if they are different. Assuming top-level for now as per new API.

                                // Try several possible locations for a hosted file URL
                                const fileUrl =
                                  item.prescriptionFileUrl ||
                                  item.fileUrl ||
                                  item.attachmentUrl ||
                                  (viewingMed as any).prescriptionFileUrl ||
                                  (viewingMed as any).fileUrl ||
                                  (viewingMed as any).attachmentUrl ||
                                  (viewingMed as any).prescriptionUrl;

                                // fallback to a locally-created object URL for files uploaded just now
                                const localEntry =
                                  viewingMed &&
                                  (localFileUrlsRef.current as any)[
                                  (viewingMed as any).id
                                  ];

                                const resolved = fileUrl
                                  ? {
                                    url: fileUrl,
                                    type: undefined as string | undefined,
                                  }
                                  : localEntry;
                                // Also check top level base64
                                const hasTopLevelFile =
                                  !!viewingMed.prescriptionFileData;

                                const canView =
                                  !!resolved?.url || hasTopLevelFile;

                                const handleView = (
                                  e: React.MouseEvent,
                                  resolvedEntry:
                                    | { url: string; type?: string }
                                    | undefined,
                                ) => {
                                  try {
                                    e.stopPropagation();
                                    e.preventDefault();
                                  } catch (err) {
                                    /* noop */
                                  }

                                  // If we have top level file data, use that first?
                                  if (hasTopLevelFile) {
                                    const type =
                                      viewingMed.prescriptionFileType ||
                                      "image/jpeg";
                                    const src = `data:${type};base64,${viewingMed.prescriptionFileData}`;
                                    const isImage =
                                      type.startsWith("image/") ||
                                      /\.(png|jpe?g|gif|webp|bmp)$/i.test(
                                        viewingMed.prescriptionFileName || "",
                                      );

                                    if (isImage) {
                                      setImagePreviewSrc(src);
                                      setImagePreviewOpen(true);
                                      return;
                                    } else {
                                      try {
                                        const byteCharacters = atob(
                                          viewingMed.prescriptionFileData || "",
                                        );
                                        const byteNumbers = new Array(
                                          byteCharacters.length,
                                        );
                                        for (
                                          let i = 0;
                                          i < byteCharacters.length;
                                          i++
                                        ) {
                                          byteNumbers[i] =
                                            byteCharacters.charCodeAt(i);
                                        }
                                        const byteArray = new Uint8Array(
                                          byteNumbers,
                                        );
                                        const blob = new Blob([byteArray], {
                                          type: type,
                                        });
                                        const blobUrl =
                                          URL.createObjectURL(blob);
                                        window.open(blobUrl, "_blank");
                                        return;
                                      } catch (e) {
                                        console.error(e);
                                      }
                                    }
                                  }

                                  if (!resolvedEntry || !resolvedEntry.url)
                                    return;
                                  const lower = String(
                                    resolvedEntry.url,
                                  ).toLowerCase();
                                  const isImage =
                                    (resolvedEntry.type &&
                                      resolvedEntry.type.startsWith(
                                        "image/",
                                      )) ||
                                    /\.(png|jpe?g|gif|webp|bmp)$/i.test(
                                      lower,
                                    ) ||
                                    lower.includes("image/");
                                  if (isImage) {
                                    setImagePreviewSrc(resolvedEntry.url);
                                    setImagePreviewOpen(true);
                                    return;
                                  }
                                  // open non-image files in a new tab/window
                                  window.open(
                                    resolvedEntry.url,
                                    "_blank",
                                    "noopener,noreferrer",
                                  );
                                };

                                return (
                                  <TableRow key={idx}>
                                    <TableCell
                                      sx={{
                                        fontWeight: "bold",
                                        wordBreak: "break-word",
                                        minWidth: 200,
                                      }}
                                    >
                                      {item.medicationName}
                                    </TableCell>
                                    <TableCell sx={{ wordBreak: "break-word" }}>
                                      {item.dosage}
                                    </TableCell>
                                    <TableCell>{item.route}</TableCell>
                                    <TableCell>{item.frequency}</TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                                      {formatToUIDate(item.startDate) || "N/A"}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                                      {formatToUIDate(item.endDate) || "N/A"}
                                    </TableCell>
                                    <TableCell align="center">
                                      {item.quantity || "-"}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600, color: (typeof item.receivedQuantity === "number" && item.receivedQuantity > 0) ? "#15803D" : "#64748B" }}>
                                      {typeof item.receivedQuantity === "number" ? item.receivedQuantity : (viewingMed.status === "RECEIVED" ? (item.quantity || "0") : 0)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600, color: (typeof item.remainingQuantity === "number" && item.remainingQuantity > 0) ? "#B45309" : "#15803D" }}>
                                      {typeof item.remainingQuantity === "number" ? item.remainingQuantity : (viewingMed.status === "RECEIVED" ? 0 : (item.quantity || "-"))}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 600, color: "#334155" }}>
                                      {item.updatedStock !== undefined ? item.updatedStock : "-"}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                                      {formatToUIDate(item.medicationExpirationDate) || "N/A"}
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip
                                        title={
                                          canView
                                            ? "View Prescription"
                                            : "No Prescription"
                                        }
                                      >
                                        <span>
                                          <Button
                                            size="small"
                                            startIcon={
                                              <VisibilityIcon fontSize="small" />
                                            }
                                            onClick={(e) =>
                                              handleView(e, resolved)
                                            }
                                            disabled={!canView}
                                            sx={{ minWidth: "auto" }}
                                          >
                                            View
                                          </Button>
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              },
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, display: "flex", justifyContent: "space-between" }}>
          <Box>
            {viewingMed && ["ORDER_MAIL_SENT", "PARTIALLY_RECEIVED"].includes(String(viewingMed.status || "").toUpperCase()) && (
              <Button
                variant="contained"
                startIcon={<LocalShippingIcon />}
                onClick={() => {
                  handleOpenReceiveModal(viewingMed);
                }}
                sx={{
                  backgroundColor: "#48CAE4",
                  color: "#000000",
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "8px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#3ab3cc",
                    boxShadow: "none",
                  },
                }}
              >
                Receive Stock
              </Button>
            )}
          </Box>
          <Button onClick={() => setViewingId(null)} sx={{ textTransform: "none" }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ─────────────────────────────────────────────────────────────
          RECEIVE MEDICATION ORDER MODAL
          ───────────────────────────────────────────────────────────── */}
      <Dialog
        open={receiveModalOpen}
        onClose={() => !receiveSubmitting && setReceiveModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 2,
            pt: 2.5,
            px: 3,
            borderBottom: "1px solid #E2E8F0",
            bgcolor: "#F8FAFC",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: "#E0F2FE",
                color: "#0284C7",
                width: 42,
                height: 42,
              }}
            >
              <LocalShippingIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#1E293B", lineHeight: 1.2 }}>
                Receive Medication Delivery
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748B" }}>
                Inspect ordered vs. received stock and check in delivery from pharmacy
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={() => !receiveSubmitting && setReceiveModalOpen(false)}
            disabled={receiveSubmitting}
            sx={{ color: "#94A3B8", "&:hover": { color: "#1E293B" } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: "#FFFFFF" }}>
          {receiveModalError && (
            <Alert
              severity="error"
              onClose={() => setReceiveModalError(null)}
              sx={{
                mb: 2.5,
                borderRadius: "10px",
                fontWeight: 600,
                fontSize: "0.85rem",
              }}
            >
              {receiveModalError}
            </Alert>
          )}

          {receivingOrder && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* Order Info Card */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: "10px",
                  bgcolor: "#F8FAFC",
                  borderColor: "#E2E8F0",
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>
                      Order Number
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0066cc", fontFamily: "monospace" }}>
                      {receivingOrder.orderNumber || receivingOrder.id?.slice(0, 8)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>
                      Resident
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#1E293B" }}>
                      {receivingOrder.residentFullName ||
                        (() => {
                          const res = residents.find((r) => r.id === receivingOrder.residentId);
                          if (res) return `${res.firstName || ""} ${res.lastName || ""}`.trim() || "N/A";
                          return receivingOrder.residentName || "N/A";
                        })()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 600, display: "block" }}>
                      Pharmacy
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#1E293B" }}>
                      {receivingOrder.pharmacyName || "-"}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Items Table Bar */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1E293B" }}>
                  Medication Items ({receivingOrder.medicationItems?.length || 0})
                </Typography>
              </Box>

              {/* Items Table */}
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "10px", borderColor: "#E2E8F0" }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#F1F5F9" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: "#475569", fontSize: "0.78rem" }}>Medication & Dosage</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#475569", fontSize: "0.78rem" }}>Ordered</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#475569", fontSize: "0.78rem" }}>Received Stock</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#475569", fontSize: "0.78rem" }}>Remaining</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#475569", fontSize: "0.78rem" }}>Live Stock</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: "#0066cc", fontSize: "0.78rem", minWidth: 150 }}>
                        Receiving Now *
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(receivingOrder.medicationItems || []).map((item, idx) => {
                      const idKey = item.id || item.medicationName;
                      const ordered = parseInt(item.quantity || "0", 10) || 0;
                      const receivedSoFar = typeof item.receivedQuantity === "number" ? item.receivedQuantity : 0;
                      const remaining = typeof item.remainingQuantity === "number"
                        ? item.remainingQuantity
                        : Math.max(0, ordered - receivedSoFar);
                      const currentVal = receiveQuantities[idKey] ?? 0;
                      const isComplete = remaining === 0;
                      const hasError = !!receiveErrors[idKey];

                      return (
                        <TableRow key={idKey || idx} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#1E293B" }}>
                              {item.medicationName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748B" }}>
                              {item.dosage} {item.route ? `• ${item.route}` : ""} {item.frequency ? `• ${item.frequency}` : ""}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={ordered} size="small" sx={{ fontWeight: 600, bgcolor: "#F1F5F9", fontSize: "0.78rem" }} />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: receivedSoFar > 0 ? "#15803D" : "#64748B" }}>
                              {receivedSoFar}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 700, color: remaining > 0 ? "#B45309" : "#15803D" }}>
                              {remaining}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>
                              {item.updatedStock !== undefined ? item.updatedStock : "-"}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {isComplete ? (
                              <Chip
                                icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
                                label="Completed"
                                size="small"
                                sx={{
                                  bgcolor: "#DCFCE7",
                                  color: "#15803D",
                                  fontWeight: 700,
                                  fontSize: "0.72rem",
                                  border: "1px solid #86EFAC",
                                }}
                              />
                            ) : (
                              <TextField
                                size="small"
                                type="number"
                                value={currentVal}
                                onChange={(e) => {
                                  const valStr = e.target.value;
                                  const num = valStr === "" ? "" : Math.max(0, parseInt(valStr, 10) || 0);
                                  setReceiveQuantities((prev) => ({
                                    ...prev,
                                    [idKey]: num,
                                  }));
                                  if (receiveErrors[idKey]) {
                                    setReceiveErrors((prev) => {
                                      const next = { ...prev };
                                      delete next[idKey];
                                      return next;
                                    });
                                  }
                                  if (receiveModalError) {
                                    setReceiveModalError(null);
                                  }
                                }}
                                inputProps={{
                                  min: 0,
                                  max: remaining,
                                  style: { textAlign: "center", fontWeight: 700 },
                                }}
                                error={hasError}
                                helperText={receiveErrors[idKey]}
                                disabled={receiveSubmitting}
                                sx={{
                                  width: 110,
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    bgcolor: "#FFFFFF",
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#0066cc",
                                    },
                                  },
                                }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Delivery Notes */}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "#475569", mb: 0.5, display: "block" }}>
                  Delivery Notes / Invoice Reference (Optional)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  placeholder="e.g. Delivered batch from VN Pharmacy invoice #9823, received by Nurse Sarah"
                  value={receiveNotes}
                  onChange={(e) => setReceiveNotes(e.target.value)}
                  disabled={receiveSubmitting}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      bgcolor: "#FFFFFF",
                    },
                  }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #E2E8F0",
            bgcolor: "#F8FAFC",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => !receiveSubmitting && setReceiveModalOpen(false)}
            disabled={receiveSubmitting}
            sx={{ textTransform: "none", color: "#64748B", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmReceive}
            disabled={receiveSubmitting}
            startIcon={receiveSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
            sx={{
              backgroundColor: "#48CAE4",
              color: "#000000",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              borderRadius: "8px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#3ab3cc",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                bgcolor: "rgba(0, 0, 0, 0.12)",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {receiveSubmitting ? "Receiving Stock..." : "Confirm & Receive"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={imagePreviewOpen}
        onClose={() => {
          setImagePreviewOpen(false);
          setImagePreviewSrc(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Prescription Preview</DialogTitle>
        <DialogContent dividers>
          {imagePreviewSrc ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <img
                src={imagePreviewSrc}
                alt="Prescription"
                style={{ maxWidth: "100%", maxHeight: "70vh" }}
              />
            </Box>
          ) : (
            <Typography>No preview available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setImagePreviewOpen(false);
              setImagePreviewSrc(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <SignatureInput
        open={signatureModalOpen}
        onClose={() => {
          setSignatureModalOpen(false);
          setSignatureOrderId(null);
        }}
        onSave={handleSignatureSave}
        loading={signingLoading}
      />

      <Dialog
        open={Boolean(deleteConfirmationId)}
        onClose={() => setDeleteConfirmationId(null)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this medication order?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteConfirmationId(null)}
            disabled={loadingAction?.type === "DELETE"}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={loadingAction?.type === "DELETE"}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, bgcolor: '#DC2626', '&:hover': { bgcolor: '#B91C1C' }, px: 3 }}
          >
            {loadingAction?.type === "DELETE" ? <CircularProgress size={24} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderMedicationPage;